import { inject } from '@angular/core';
import { CanActivateFn, UrlTree, Router } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { Store } from '@ngrx/store';
import { loadCountry } from '@sharedModule/store/country/country.actions';
import { loadSourceCountry } from '@sharedModule/store/source-country/source-country.actions';
import { Observable, map, catchError, of } from 'rxjs';

export const AuthGuard: CanActivateFn = ():
  | Observable<boolean | UrlTree>
  | boolean
  | UrlTree => {
  const router = inject(Router);
  const store = inject(Store);
  const authService = inject(AuthenticationService);
  const partnerService = inject(PartnerService);
  const sessionStorageService = inject(SessionStorageService);

  if (!authService.isLoggedIn()) {
    return router.createUrlTree(['/auth/login']);
  }

  return authService.getUserPermissions().pipe(
    map(res => {
      try {
        const { partnerLogo, userDetails, userPermissionDetails } =
          res.response;

        // Store super admin status
        sessionStorageService.set(
          'isSuperAdmin',
          JSON.stringify(userDetails.is_super_admin)
        );

        // Store user data
        const authUser = authService.createAuthUser(userDetails, partnerLogo);
        authService.userInfoSubject.next(authUser);

        // Set user permissions
        const permissions = userPermissionDetails?.permissions;
        if (permissions) {
          authService.permissionsSubject.next(permissions);
        }

        // Load country data
        store.dispatch(loadCountry());

        // Load partner or source country data based on admin status
        if (authService.isSuperAdmin()) {
          partnerService.getPartnerListDropdown().subscribe({
            error: error =>
              console.error('Failed to load partner dropdown:', error),
          });
        } else {
          store.dispatch(loadSourceCountry({}));
        }

        return true;
      } catch (error) {
        console.error('Error in AuthGuard:', error);
        return router.createUrlTree(['/auth/login']);
      }
    }),
    catchError(error => {
      console.error('Failed to get user permissions:', error);
      authService.logout();
      return of(router.createUrlTree(['/auth/login']));
    })
  );
};
